// IndexedDB-backed local storage via idb-keyval, plus localStorage for the device token.
// Storage layout:
//   localStorage["kiosk.token"]      → opaque device token (single string)
//   localStorage["kiosk.deviceMeta"] → JSON { deviceId, companyId, deviceName, fingerprint }
//   IDB "employees"                  → { id, identification, fullName }[]
//   IDB "queue"                      → ordered FIFO of pending marks
//   IDB "serverOffsetMs"             → number (server - device, ms)
//   IDB "lastSyncAt"                 → ISO string

import { get, set, del, createStore } from 'idb-keyval';

const KIOSK_STORE = createStore('planillapro-kiosk', 'kv');

const TOKEN_KEY = 'kiosk.token';
const META_KEY = 'kiosk.deviceMeta';
const FP_KEY = 'kiosk.fingerprint';

// ---------- Token & device metadata (synchronous via localStorage) ----------

export function getDeviceToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setDeviceToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearDeviceToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function getDeviceMeta() {
  try { return JSON.parse(localStorage.getItem(META_KEY) || 'null'); }
  catch { return null; }
}

export function setDeviceMeta(meta) {
  localStorage.setItem(META_KEY, JSON.stringify(meta));
}

export function clearDeviceMeta() {
  localStorage.removeItem(META_KEY);
}

// Stable fingerprint: random uuid persisted in localStorage.
// Re-installing the browser clears this — backend treats it as informational only.
export function getOrCreateFingerprint() {
  let fp = localStorage.getItem(FP_KEY);
  if (!fp) {
    fp = (crypto.randomUUID && crypto.randomUUID()) ||
      [...crypto.getRandomValues(new Uint8Array(16))].map(b => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem(FP_KEY, fp);
  }
  return fp;
}

// ---------- Employees cache (IndexedDB) ----------

export async function setEmployees(list) {
  await set('employees', list, KIOSK_STORE);
}

export async function getEmployees() {
  return (await get('employees', KIOSK_STORE)) || [];
}

export async function clearEmployees() {
  await del('employees', KIOSK_STORE);
}

// ---------- Server time offset ----------

export async function setServerOffsetMs(offsetMs) {
  await set('serverOffsetMs', offsetMs, KIOSK_STORE);
}

export async function getServerOffsetMs() {
  return (await get('serverOffsetMs', KIOSK_STORE)) || 0;
}

export async function setLastSyncAt(iso) {
  await set('lastSyncAt', iso, KIOSK_STORE);
}

export async function getLastSyncAt() {
  return await get('lastSyncAt', KIOSK_STORE);
}

// ---------- Mark queue (FIFO) ----------

export async function queueGetAll() {
  return (await get('queue', KIOSK_STORE)) || [];
}

export async function queueSetAll(list) {
  await set('queue', list, KIOSK_STORE);
}

export async function queuePush(item) {
  const list = await queueGetAll();
  list.push(item);
  await queueSetAll(list);
}

export async function queueLength() {
  const list = await queueGetAll();
  return list.length;
}

/** Remove items by clientUuid (after server ACK). */
export async function queueRemove(clientUuids) {
  if (!clientUuids?.length) return;
  const set2 = new Set(clientUuids);
  const list = await queueGetAll();
  await queueSetAll(list.filter(it => !set2.has(it.clientUuid)));
}

// ---------- Bulk reset (logout) ----------

export async function clearAll() {
  clearDeviceToken();
  clearDeviceMeta();
  await del('employees', KIOSK_STORE);
  await del('queue', KIOSK_STORE);
  await del('serverOffsetMs', KIOSK_STORE);
  await del('lastSyncAt', KIOSK_STORE);
}
